<template>
  <b-card class="waw-card">
    <div class="waw-head">
      <div class="waw-eyebrow">{{ eyebrow }}</div>
      <h4 class="waw-title">{{ title }}</h4>
    </div>

    <!-- The copy trio. Rendered by the FRAME, from wizardCopy.js, so no screen can ship without
         help text and no screen can grow a second, differently-worded panel of its own. -->
    <div v-if="copy" class="waw-help">
      <div class="waw-help__item">
        <div class="waw-help__label">What this is</div>
        <p class="waw-help__text">{{ copy.whatThis }}</p>
      </div>
      <div class="waw-help__item">
        <div class="waw-help__label">What happens next</div>
        <p class="waw-help__text">{{ copy.whatHappens }}</p>
      </div>
      <div class="waw-help__item">
        <div class="waw-help__label">What you&rsquo;ll need</div>
        <p class="waw-help__text">{{ copy.whatYouNeed }}</p>
      </div>
    </div>

    <div class="waw-body">
      <slot />
    </div>

    <div v-if="showBack || showContinue || showRefresh" class="waw-actions">
      <b-button
        v-if="showBack"
        variant="link"
        class="waw-back"
        :data-test="testId('back')"
        :disabled="busy"
        @click="$emit('back')"
      >
        Back
      </b-button>

      <b-button
        v-if="showRefresh"
        variant="link"
        class="waw-refresh"
        :data-test="testId('refresh')"
        :disabled="busy"
        @click="$emit('refresh')"
      >
        Refresh status
      </b-button>

      <b-button
        v-if="showContinue"
        variant="primary"
        class="waw-cta"
        :data-test="testId('continue')"
        :disabled="busy || !canContinue"
        @click="$emit('continue')"
      >
        <span v-if="busy"><b-spinner small class="mr-2" />Working&hellip;</span>
        <span v-else>{{ continueLabel }}</span>
      </b-button>
    </div>
  </b-card>
</template>

<script>
/**
 * The frame every wizard screen renders inside: eyebrow + title, the copy trio, the screen's own
 * body, and the navigation.
 *
 * ── WHY NAVIGATION LIVES HERE AND NOT IN THE STEPS ───────────────────────────────────────────────
 * Two reasons, and the second is the important one.
 *
 * 1. Every screen's Back/Continue/Refresh would otherwise be ten near-identical button blocks,
 *    which is ten chances for one of them to forget its busy guard and let a double-click fire two
 *    requests.
 * 2. `data-test` ids are DERIVED from the step key (`waw-<step>-continue`), not typed. The wizard
 *    is QA-driven through Playwright, so a screen whose Continue button carried a typo'd or absent
 *    test id would be invisible to the driver — and the way that failure presents is "the wizard is
 *    broken", days later, mid-run. Generating them makes the naming convention true by
 *    construction; the node-test then only has to check the controls each screen adds ITSELF.
 *
 * The frame is deliberately dumb: it renders and emits. Which buttons appear, whether Continue is
 * enabled, and what any of them do are all decided by WhatsAppWizard.vue, which is the one place
 * that knows the server state.
 */
import { wizardCopy } from './wizardCopy';

export default {
  name: 'WizardStepFrame',

  props: {
    /** The wizard step key — drives both the copy lookup and every generated data-test id. */
    stepKey: { type: String, required: true },
    title: { type: String, required: true },
    eyebrow: { type: String, default: '' },
    /** A request is in flight: every control is disabled, Continue shows a spinner. */
    busy: { type: Boolean, default: false },
    canContinue: { type: Boolean, default: false },
    continueLabel: { type: String, default: 'Continue' },
    showBack: { type: Boolean, default: false },
    showContinue: { type: Boolean, default: false },
    showRefresh: { type: Boolean, default: false },
  },

  computed: {
    /** Null for a key with no copy — which the copy-completeness node-test makes unreachable. */
    copy() {
      return wizardCopy(this.stepKey);
    },
  },

  methods: {
    testId(control) {
      return `waw-${this.stepKey}-${control}`;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Skinned from the SHARED My Account tab tokens, exactly as WhatsAppTab, SmsCreditsTab and
   Patient Payments are, so the wizard reads as part of the same product. */
@import '../PatientPayments/tokens';

.waw-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}

.waw-head {
  margin-bottom: 1.1rem;
}

.waw-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $pp-text-subtle;
  margin-bottom: 0.35rem;
}

.waw-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: $pp-text-strong;
  margin: 0;
}

/* The copy trio. Sunken panel so it reads as reference material sitting beside the task, not as
   part of the form the practice has to fill in. */
.waw-help {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1.4rem;
  border-radius: $pp-radius-sm;
  background: $pp-surface-sunken;
  border: 1px solid $pp-border;
}

.waw-help__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pp-brand;
  margin-bottom: 0.3rem;
}

.waw-help__text {
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.6;
  color: $pp-text-muted;
}

.waw-body {
  font-size: 0.9rem;
  color: $pp-text-muted;
}

.waw-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.1rem;
  border-top: 1px solid $pp-border;
}

/* Push the primary action to the right; the link-styled ones stay left. */
.waw-cta {
  margin-left: auto;
  font-weight: 700;
  border-radius: $pp-radius-sm;

  &.btn-primary {
    background-color: $pp-brand;
    border-color: $pp-brand;
    color: #fff;

    &:hover:not(:disabled),
    &:focus:not(:disabled),
    &:not(:disabled):not(.disabled):active {
      background-color: $pp-brand-deep;
      border-color: $pp-brand-deep;
      color: #fff;
    }
  }
}

/* BootstrapVue's .btn-link does not carry this component's scope attribute, so it is pierced with
   ::v-deep kept UNDER a scoped ancestor — containment holds (repo CSS rule). */
.waw-actions {
  ::v-deep .btn-link {
    padding: 0;
    font-size: 0.85rem;
    font-weight: 600;
    color: $pp-brand;

    &:hover { color: $pp-brand-deep; }
  }
}

/* Three columns of help text never fit on a phone, and neither does a row of actions. */
@media (max-width: 767.98px) {
  .waw-help {
    grid-template-columns: minmax(0, 1fr);
    gap: 0.85rem;
  }
}
</style>
